import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Download, AlertTriangle } from 'lucide-react';
import { documentAPI } from '../../lib/api';
import { Button } from '../ui/button';

// The worker is copied to public/ at build time and served statically, so there is no bundler
// resolution to go wrong. Keep the version in step with the pdfjs-dist dependency.
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Shared bilag viewer — used by BOTH the single-screen review and the documents modal.
// PDFs render to our own <canvas> via pdf.js rather than an <iframe>, so the browser's built-in
// PDF toolbar and thumbnail rail never appear: our zoom/page controls are the only chrome, and
// the full pane width is the document.
export function BilagViewer({ docId, filename, fileType, className = '' }) {
    const [url, setUrl] = useState(null);
    const [blob, setBlob] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const [zoom, setZoom] = useState(1);
    const [page, setPage] = useState(1);
    const [numPages, setNumPages] = useState(0);

    const canvasRef = useRef(null);
    const pdfRef = useRef(null);
    const renderTaskRef = useRef(null);

    const isPdf = (fileType === 'pdf') || (blob && blob.type === 'application/pdf')
        || (filename || '').toLowerCase().endsWith('.pdf');

    // ── Fetch the file (auth header travels with the API client) ──
    useEffect(() => {
        if (!docId) return;
        let active = true;
        let objUrl = null;
        setLoading(true); setError(null); setUrl(null); setBlob(null);
        setPage(1); setZoom(1); setNumPages(0); pdfRef.current = null;
        documentAPI.getFile(docId)
            .then((res) => {
                if (!active) return;
                objUrl = URL.createObjectURL(res.data);
                setBlob(res.data);
                setUrl(objUrl);
            })
            .catch((err) => {
                if (active) setError(err.response?.status === 403
                    ? 'Du har ikke adgang til denne fil.'
                    : 'Kunne ikke hente bilaget.');
            })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
    }, [docId]);

    // ── Load the PDF document once per file ──
    useEffect(() => {
        if (!url || !isPdf) return;
        let cancelled = false;
        (async () => {
            try {
                const task = pdfjsLib.getDocument({ url });
                const pdf = await task.promise;
                if (cancelled) { pdf.destroy?.(); return; }
                pdfRef.current = pdf;
                setNumPages(pdf.numPages);
                setPage(1);
            } catch {
                if (!cancelled) setError('Kunne ikke vise PDF-filen.');
            }
        })();
        return () => { cancelled = true; };
    }, [url, isPdf]);

    // ── Render the current page at the current zoom ──
    const renderPage = useCallback(async () => {
        const pdf = pdfRef.current;
        const canvas = canvasRef.current;
        if (!pdf || !canvas) return;
        try {
            if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
            const p = await pdf.getPage(Math.min(Math.max(page, 1), pdf.numPages));
            // Render at device pixel ratio so text stays crisp when zoomed.
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const viewport = p.getViewport({ scale: zoom * 1.5 * dpr });
            const ctx = canvas.getContext('2d');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            canvas.style.width = `${viewport.width / dpr}px`;
            canvas.style.height = `${viewport.height / dpr}px`;
            const task = p.render({ canvasContext: ctx, viewport });
            renderTaskRef.current = task;
            await task.promise;
            renderTaskRef.current = null;
        } catch (e) {
            if (e?.name !== 'RenderingCancelledException') setError('Kunne ikke tegne siden.');
        }
    }, [page, zoom]);

    useEffect(() => { if (isPdf && numPages) renderPage(); }, [isPdf, numPages, renderPage]);

    useEffect(() => () => { if (renderTaskRef.current) renderTaskRef.current.cancel(); }, []);

    const download = () => {
        if (!url) return;
        const a = document.createElement('a');
        a.href = url; a.download = filename || 'bilag';
        document.body.appendChild(a); a.click(); a.remove();
    };

    return (
        <div className={`flex flex-col min-h-0 ${className}`} data-testid="bilag-viewer">
            {/* Our controls are the ONLY chrome */}
            <div className="flex items-center gap-2 px-3 py-2 border-b bg-background shrink-0">
                <Button type="button" size="sm" variant="outline" className="h-8 px-2"
                        onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                        data-testid="viewer-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
                <span className="text-xs tabular-nums w-12 text-center">{Math.round(zoom * 100)}%</span>
                <Button type="button" size="sm" variant="outline" className="h-8 px-2"
                        onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))}
                        data-testid="viewer-zoom-in"><ZoomIn className="w-4 h-4" /></Button>

                {isPdf && numPages > 1 && (
                    <div className="flex items-center gap-1 ml-2">
                        <Button type="button" size="sm" variant="outline" className="h-8 px-2"
                                onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                                data-testid="viewer-page-prev"><ChevronLeft className="w-4 h-4" /></Button>
                        <span className="text-xs tabular-nums whitespace-nowrap">
                            Side {page} / {numPages}
                        </span>
                        <Button type="button" size="sm" variant="outline" className="h-8 px-2"
                                onClick={() => setPage((p) => Math.min(numPages, p + 1))}
                                disabled={page >= numPages}
                                data-testid="viewer-page-next"><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                )}

                <Button type="button" size="sm" variant="ghost" className="h-8 px-2 ml-auto"
                        onClick={download} title="Download" data-testid="viewer-download">
                    <Download className="w-4 h-4" />
                </Button>
            </div>

            <div className="flex-1 overflow-auto bg-muted/30 p-3 min-h-0">
                {loading && <p className="text-sm text-muted-foreground text-center py-10">Henter bilag…</p>}
                {error && (
                    <div className="flex items-center gap-2 text-sm text-destructive py-6 justify-center">
                        <AlertTriangle className="h-4 w-4" /> {error}
                    </div>
                )}
                {!loading && !error && url && (
                    isPdf ? (
                        <canvas ref={canvasRef} className="mx-auto bg-white shadow-sm" data-testid="viewer-canvas" />
                    ) : (
                        <img alt={filename || 'bilag'} src={url} data-testid="viewer-image"
                             style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
                             className="mx-auto bg-white shadow-sm max-w-full" />
                    )
                )}
            </div>
        </div>
    );
}

export default BilagViewer;
